/**
 * useOnlineStatus Hook
 *
 * Enhanced hook to detect online/offline status with optional ping verification.
 * Provides reactive state updates on network changes with debouncing.
 *
 * @module hooks/useOnlineStatus
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Configuration options for the online status hook
 */
export interface UseOnlineStatusOptions {
  /**
   * URL to ping for more reliable online detection.
   * If provided, verifies connectivity beyond navigator.onLine.
   * Should be a fast, reliable endpoint (e.g., /api/v1/health).
   */
  pingUrl?: string;

  /**
   * Interval in milliseconds for periodic ping checks.
   * Only used if pingUrl is provided.
   * @default undefined (no periodic checks)
   */
  pingInterval?: number;

  /**
   * Timeout in milliseconds for ping requests.
   * @default 5000
   */
  pingTimeout?: number;

  /**
   * Debounce delay in milliseconds to avoid rapid state changes.
   * @default 100
   */
  debounceDelay?: number;
}

/**
 * Return value of the useOnlineStatus hook
 */
export interface UseOnlineStatusReturn {
  /**
   * Whether the app is currently online.
   * If pingUrl is configured, this reflects actual connectivity.
   */
  isOnline: boolean;

  /**
   * Timestamp of the last connectivity check.
   * Null if no check has been performed yet.
   */
  lastChecked: Date | null;

  /**
   * Whether a ping check is currently in progress.
   */
  isChecking: boolean;

  /**
   * Manually trigger a connectivity check.
   * Useful for verifying connection before critical operations.
   */
  checkOnline: () => Promise<boolean>;
}

const DEFAULT_PING_TIMEOUT = 5000;
const DEFAULT_DEBOUNCE_DELAY = 100;

/**
 * Hook to detect online/offline status with optional ping verification.
 *
 * @param options - Configuration options
 * @returns Online status and utility functions
 *
 * @example
 * ```tsx
 * // Basic usage - browser events only
 * function Dashboard() {
 *   const { isOnline } = useOnlineStatus();
 *
 *   return (
 *     <div>
 *       {!isOnline ? <OfflineBanner /> : null}
 *       <DashboardContent />
 *     </div>
 *   );
 * }
 *
 * // With ping verification
 * function App() {
 *   const { isOnline, checkOnline } = useOnlineStatus({
 *     pingUrl: '/api/v1/health',
 *     pingInterval: 30000, // Check every 30s
 *   });
 *
 *   const handleSync = async () => {
 *     const online = await checkOnline();
 *     if (online) {
 *       await syncData();
 *     }
 *   };
 * }
 * ```
 */
export function useOnlineStatus(
  options?: UseOnlineStatusOptions
): UseOnlineStatusReturn {
  const {
    pingUrl,
    pingInterval,
    pingTimeout = DEFAULT_PING_TIMEOUT,
    debounceDelay = DEFAULT_DEBOUNCE_DELAY,
  } = options ?? {};

  const [isOnline, setIsOnline] = useState<boolean>(() => navigator.onLine);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [isChecking, setIsChecking] = useState<boolean>(false);

  // Refs for cleanup and debouncing
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);

  /**
   * Debounced state update to prevent rapid changes
   */
  const updateOnlineStatus = useCallback(
    (online: boolean) => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setIsOnline(online);
          setLastChecked(new Date());
        }
      }, debounceDelay);
    },
    [debounceDelay]
  );

  /**
   * Check connectivity by pinging the configured URL
   */
  const pingCheck = useCallback(async (): Promise<boolean> => {
    if (!pingUrl) {
      return navigator.onLine;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current !== null) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(pingUrl, {
        method: 'HEAD',
        cache: 'no-store',
        signal: abortControllerRef.current.signal,
        // Add timeout using AbortSignal.timeout if available
        ...(typeof AbortSignal !== 'undefined' &&
        'timeout' in AbortSignal &&
        typeof AbortSignal.timeout === 'function'
          ? {}
          : {}),
      });

      // Set up manual timeout if AbortSignal.timeout not available
      const timeoutId = setTimeout(() => {
        abortControllerRef.current?.abort();
      }, pingTimeout);

      const result = response.ok;
      clearTimeout(timeoutId);
      return result;
    } catch (error) {
      // Network error or aborted - treat as offline
      // AbortError means we intentionally cancelled, not a network issue
      if (error instanceof Error && error.name === 'AbortError') {
        return navigator.onLine;
      }
      return false;
    }
  }, [pingUrl, pingTimeout]);

  /**
   * Manually check online status
   */
  const checkOnline = useCallback(async (): Promise<boolean> => {
    // If browser says offline, trust it immediately
    if (!navigator.onLine) {
      updateOnlineStatus(false);
      return false;
    }

    setIsChecking(true);

    try {
      const online = await pingCheck();
      updateOnlineStatus(online);
      return online;
    } finally {
      if (isMountedRef.current) {
        setIsChecking(false);
      }
    }
  }, [pingCheck, updateOnlineStatus]);

  /**
   * Handle browser online event
   */
  const handleOnline = useCallback(() => {
    // Browser says online - verify with ping if configured
    if (pingUrl) {
      void checkOnline();
    } else {
      updateOnlineStatus(true);
    }
  }, [pingUrl, checkOnline, updateOnlineStatus]);

  /**
   * Handle browser offline event
   */
  const handleOffline = useCallback(() => {
    // Browser says offline - trust it immediately
    updateOnlineStatus(false);
  }, [updateOnlineStatus]);

  // Set up event listeners and initial check
  useEffect(() => {
    isMountedRef.current = true;

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    void checkOnline();

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      // Clean up debounce timer
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }

      // Cancel any in-flight request
      if (abortControllerRef.current !== null) {
        abortControllerRef.current.abort();
      }
    };
  }, [handleOnline, handleOffline, checkOnline]);

  // Set up periodic ping checks
  useEffect(() => {
    if (!pingInterval || pingInterval <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      void checkOnline();
    }, pingInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [pingInterval, checkOnline]);

  return {
    isOnline,
    lastChecked,
    isChecking,
    checkOnline,
  };
}
